import { useEffect, useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { Card, Button, Title, Text, Icon } from '@nimbus-ds/components'
import { funnelService, type ProductsOriginsTimelineResponse } from '@/api/funnel'
import { ShoppingCartIcon } from '@nimbus-ds/icons'

type Metric = 'views' | 'conversions' | 'conversion_rate' | 'revenue'
type Smoothing = 'raw' | 'ma3'

const METRIC_LABELS: Record<Metric, string> = {
  views: 'Vistas',
  conversions: 'Compras',
  conversion_rate: '% Compra',
  revenue: 'Revenue',
}

const SMOOTHING_LABELS: Record<Smoothing, string> = {
  raw: 'Diario',
  ma3: '3d avg',
}

interface ProductPerformanceTimelineProps {
  storeId: string
  windowDays?: number
  topN?: number
  initialData?: ProductsOriginsTimelineResponse | null
}

const LINE_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
]

// Stacking conversion_rate would imply the rates sum, which they don't.
const CAN_STACK: Record<Metric, boolean> = {
  views: true,
  conversions: true,
  revenue: true,
  conversion_rate: false,
}

export function ProductPerformanceTimeline({
  storeId,
  windowDays = 7,
  topN = 5,
  initialData,
}: ProductPerformanceTimelineProps) {
  const [data, setData] = useState<ProductsOriginsTimelineResponse | null>(initialData ?? null)
  const [isLoading, setIsLoading] = useState(!initialData)
  const [error, setError] = useState<string | null>(null)
  const [metric, setMetric] = useState<Metric>('views')
  const [smoothing, setSmoothing] = useState<Smoothing>('raw')
  const [stacked, setStacked] = useState<boolean>(false)
  const [hidden, setHidden] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (initialData) {
      setData(initialData)
      setIsLoading(false)
      return
    }
    if (!storeId) return
    let cancelled = false
    setIsLoading(true)
    setError(null)
    funnelService
      .getStoreProductsOriginsTimeline(storeId, windowDays)
      .then((res) => {
        if (cancelled) return
        setData(res)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load product timeline:', err)
        setError('No se pudo cargar la evolución de productos')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [storeId, windowDays, initialData])

  const { chartData, productDefs } = useMemo(() => {
    if (!data) return { chartData: [], productDefs: [] }

    // Drop unnamed/"Unknown" products — matches ProductPerformanceTable's filter.
    const ranked = (data.products_index || [])
      .filter((p) => p.product_name && p.product_name !== 'Unknown')
      .slice(0, topN)
      .map((p, i) => ({
        ...p,
        color: LINE_COLORS[i % LINE_COLORS.length],
      }))

    type Row = Record<string, string | number | null>
    const points: Row[] = (data.series || []).map((point) => {
      const row: Row = {
        date: point.date,
        label: format(parseISO(point.date), 'dd MMM', { locale: es }),
      }
      const byId = new Map<string, (typeof point.products)[number]>()
      for (const p of point.products) byId.set(p.product_id, p)
      for (const def of ranked) {
        const p = byId.get(def.product_id)
        if (!p) {
          row[def.product_id] = metric === 'conversion_rate' ? null : 0
          continue
        }
        if (metric === 'conversion_rate') {
          row[def.product_id] = p.views > 0 ? +p.conversion_rate.toFixed(2) : null
        } else if (metric === 'views') {
          row[def.product_id] = p.views
        } else if (metric === 'conversions') {
          row[def.product_id] = p.conversions
        } else {
          row[def.product_id] = p.revenue
        }
      }
      return row
    })

    // 3-day moving average
    let series = points
    if (smoothing === 'ma3' && points.length) {
      const window = 3
      series = points.map((_, i) => {
        const out: Row = { date: points[i].date, label: points[i].label }
        for (const def of ranked) {
          let sum = 0
          let count = 0
          for (let k = Math.max(0, i - window + 1); k <= i; k++) {
            const v = points[k][def.product_id]
            if (typeof v === 'number') {
              sum += v
              count += 1
            }
          }
          if (count === 0) {
            out[def.product_id] = metric === 'conversion_rate' ? null : 0
          } else {
            const avg = sum / count
            out[def.product_id] = metric === 'conversion_rate'
              ? +avg.toFixed(2)
              : Math.round(avg * 100) / 100
          }
        }
        return out
      })
    }

    return { chartData: series, productDefs: ranked }
  }, [data, metric, smoothing, topN])

  const toggleProduct = (productId: string) => {
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(productId)) next.delete(productId)
      else next.add(productId)
      return next
    })
  }

  const canStack = CAN_STACK[metric]
  const isStacked = stacked && canStack

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v)

  const tickFormatter = (v: number) => {
    if (metric === 'conversion_rate') return `${v}%`
    if (metric === 'revenue') {
      if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
      if (v >= 1_000) return `$${Math.round(v / 1_000)}k`
      return `$${v}`
    }
    return `${v}`
  }

  const tooltipFormatter = (value: number, name: string) => {
    const def = productDefs.find((d) => d.product_id === name)
    const display = def?.product_name || name
    let formatted: string
    if (metric === 'conversion_rate') formatted = `${value}%`
    else if (metric === 'revenue') formatted = formatCurrency(value)
    else formatted = value.toLocaleString()
    return [formatted, display]
  }

  if (isLoading) {
    return (
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Icon source={<ShoppingCartIcon />} />
            <Title as="h3">Evolución por Producto</Title>
          </div>
          <Text>Métricas diarias por producto</Text>
        </Card.Header>
        <Card.Body>
          <div className="h-[280px] w-full animate-pulse rounded-md bg-muted" />
        </Card.Body>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Evolución por Producto</Title>
        </Card.Header>
        <Card.Body>
          <p className="text-sm text-red-600">{error}</p>
        </Card.Body>
      </Card>
    )
  }

  const header = (
    <Card.Header>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Icon source={<ShoppingCartIcon />} />
            <Title as="h3">Evolución por Producto</Title>
          </div>
          <Text>
            Top {productDefs.length} productos · últimos {windowDays} días
          </Text>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            appearance={isStacked ? 'primary' : 'transparent'}
            size="small"
            disabled={!canStack}
            onClick={() => setStacked((v) => !v)}
            title={canStack ? 'Apilar series' : 'No aplicable a tasas'}
          >
            Apilado
          </Button>
          <div className="flex gap-1 rounded-md border p-0.5">
            {(Object.keys(SMOOTHING_LABELS) as Smoothing[]).map((s) => (
              <Button
                key={s}
                appearance={smoothing === s ? 'primary' : 'transparent'}
                size="small"
                onClick={() => setSmoothing(s)}
              >
                {SMOOTHING_LABELS[s]}
              </Button>
            ))}
          </div>
          <div className="flex gap-1 rounded-md border p-0.5">
            {(Object.keys(METRIC_LABELS) as Metric[]).map((m) => (
              <Button
                key={m}
                appearance={metric === m ? 'primary' : 'transparent'}
                size="small"
                onClick={() => setMetric(m)}
              >
                {METRIC_LABELS[m]}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </Card.Header>
  )

  if (!chartData.length || !productDefs.length) {
    return (
      <Card>
        {header}
        <Card.Body>
          <Text className="py-8 text-center">
            Aún no hay datos diarios suficientes.
          </Text>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card>
      {header}
      <Card.Body>
        <ResponsiveContainer width="100%" height={300}>
          {isStacked ? (
            <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={tickFormatter} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend
                wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
                onClick={(entry) => {
                  if (entry?.dataKey) toggleProduct(String(entry.dataKey))
                }}
                formatter={(value) => {
                  const def = productDefs.find((d) => d.product_id === value)
                  const label = def?.product_name || value
                  const isHidden = hidden.has(String(value))
                  return (
                    <span
                      style={{
                        opacity: isHidden ? 0.4 : 1,
                        textDecoration: isHidden ? 'line-through' : 'none',
                      }}
                    >
                      {label}
                    </span>
                  )
                }}
              />
              {productDefs.map((def) => (
                <Area
                  key={def.product_id}
                  type="monotone"
                  dataKey={def.product_id}
                  stackId="stack"
                  stroke={def.color}
                  fill={def.color}
                  fillOpacity={0.45}
                  strokeWidth={1.5}
                  hide={hidden.has(def.product_id)}
                />
              ))}
            </AreaChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 12 }}
                allowDecimals={metric === 'conversion_rate'}
                tickFormatter={tickFormatter}
              />
              <Tooltip formatter={tooltipFormatter} />
              <Legend
                wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
                onClick={(entry) => {
                  if (entry?.dataKey) toggleProduct(String(entry.dataKey))
                }}
                formatter={(value) => {
                  const def = productDefs.find((d) => d.product_id === value)
                  const label = def?.product_name || value
                  const isHidden = hidden.has(String(value))
                  return (
                    <span
                      style={{
                        opacity: isHidden ? 0.4 : 1,
                        textDecoration: isHidden ? 'line-through' : 'none',
                      }}
                    >
                      {label}
                    </span>
                  )
                }}
              />
              {productDefs.map((def) => (
                <Line
                  key={def.product_id}
                  type="monotone"
                  dataKey={def.product_id}
                  stroke={def.color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  hide={hidden.has(def.product_id)}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </Card.Body>
    </Card>
  )
}

export default ProductPerformanceTimeline
